/**
 * O ESTADO DO RANKING AO VIVO NO CARD DA DISPUTA (15/10/2026, item 5 das correções do pregão aprovadas pelo Heitor).
 *
 * O selo "Ranking ao vivo indisponível — robô segurando" juntava problemas diferentes que pedem gestos diferentes:
 *  - SEM LEITURA: o sensor não lê. Não se resolve esperando: F5 na aba da disputa no VNC.
 *  - ATRASADO: o sensor lê, mas a tabela não acompanha o líder. Esperar a próxima leitura.
 * O backend distingue (motivoUso feed-morto × topo-diverge) e passa a gravar `estado` no `rankingLiveStatus`. ⛔ A tela
 * lê o CAMPO, nunca o texto do motivo. Sem o campo (backend antigo), `null` aqui e o card fica como era.
 *
 * O "não lê há Ns" é a idade da última LEITURA (`idadeBuscaMs`, medida no servidor: o contrato não depende do relógio do
 * navegador), não a do CONTEÚDO (`idadeMs`):
 * misturar as duas foi o que fez "parou de ler" para um sensor que lia (sessão 153, 05/10/2026).
 */
export type EstadoDoRanking = 'sem-leitura' | 'atrasado' | 'pelotao-invisivel' | 'motor-sem-olhos' | 'motor-sem-leitura';

export interface FaixaDoRanking {
  estado: EstadoDoRanking;
  titulo: string;
  texto: string;
}

const ESTADOS: EstadoDoRanking[] = ['sem-leitura', 'atrasado', 'pelotao-invisivel', 'motor-sem-olhos', 'motor-sem-leitura'];

/** Segundos desde a última LEITURA do sensor (`idadeBuscaMs` do servidor); null se o backend não mandou. */
export function segundosSemLer(rl: any): number | null {
  if (typeof rl?.idadeBuscaMs === 'number' && Number.isFinite(rl.idadeBuscaMs)) return Math.max(0, Math.round(rl.idadeBuscaMs / 1000));
  return null;
}

/** A faixa do card. `null` = ranking ao vivo, ou backend sem `estado` (aí vale o selo de antes). */
export function faixaDoRanking(rl: any, agoraMs: number): FaixaDoRanking | null {
  if (!rl || rl.aoVivo !== false) return null;
  const estado = rl.estado as EstadoDoRanking;
  if (!ESTADOS.includes(estado)) return null;
  switch (estado) {
    case 'sem-leitura': {
      const s = segundosSemLer(rl);
      return {
        estado,
        titulo: s != null ? `Sem ranking: o sensor não lê há ${s}s` : 'Sem ranking: o sensor não está lendo',
        texto: 'F5 na aba da disputa no VNC (navegar dentro do portal não reinjeta). Enquanto isso o robô segura a margem.',
      };
    }
    case 'atrasado':
      return {
        estado,
        titulo: 'Ranking atrasado: a tabela não acompanha o líder',
        texto: 'O sensor está lendo; a próxima leitura costuma acertar. Enquanto isso o robô segura a margem.',
      };
    case 'pelotao-invisivel':
      return {
        estado,
        titulo: 'Pelotão do meio não visível',
        texto: 'O robô só vê o líder e segura a margem: o pelotão do meio é que decidiria a posição.',
      };
    case 'motor-sem-olhos':
      return {
        estado,
        titulo: 'Começou sem olhos confirmados',
        texto: 'A aba da disputa não voltou a falar depois do pedido de reinjeção. Confira no VNC e, se preciso, F5 nela.',
      };
    case 'motor-sem-leitura':
      return {
        estado,
        titulo: 'Motor servidor sem leitura do portal',
        texto: 'O robô não está lendo o portal e não vai lançar. Confira a sessão ComprasNet e acompanhe no braço.',
      };
    default:
      return null;
  }
}
